"use client";

import { useEffect, useMemo, useState } from "react";
import { Building2, User } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { track } from "@/lib/analytics/posthog";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { Plan, PLAN_CYCLE, PlanAudience } from "@/services/plans/types";
import { PlanPricingCard } from "../components/plan-pricing-card";
import { PlanComparisonSection } from "../components/plan-comparison-section";
import { EnterprisePricingCard } from "../components/enterprise-pricing-card";
import { Grid, GridItem } from "../components/grid";
import { HOME_COPY } from "../home.copy";
import { findHighlighted, orderPlansForShowcase } from "@/lib/plans/order-plans";
import { presentPlan } from "@/lib/plans/plan-presentation";
import { resolveCycleView } from "@/lib/plans/cycle-view";

const AUDIENCE_TABS: Array<{
  id: PlanAudience;
  icon: typeof User;
}> = [
  { id: "creator", icon: User },
  { id: "agency", icon: Building2 },
];

interface PricingSectionContentProps {
  creatorPlans: Plan[];
  collabPlans: Plan[];
}

export function PricingSectionContent({
  creatorPlans,
  collabPlans,
}: PricingSectionContentProps) {
  const [audience, setAudience] = useState<PlanAudience>("creator");
  const [selectedCycle, setSelectedCycle] = useState<PLAN_CYCLE>(
    PLAN_CYCLE.MONTHLY
  );
  // A lista de opcionais é longa e abre nas três colunas de uma vez: o estado
  // mora aqui, e não em cada card, para as colunas nunca ficarem com alturas
  // diferentes e para a comparação item a item continuar valendo.
  const [featuresExpanded, setFeaturesExpanded] = useState(false);

  useEffect(() => {
    track(ANALYTICS_EVENTS.PLANOS_VISUALIZADOS);
  }, []);

  const plans = audience === "agency" ? collabPlans : creatorPlans;

  // Um plano pago pode existir só no ciclo mensal. Nesse caso o switch some em
  // vez de deixar o usuário escolher "Anual" e ver um preço mensal. O gratuito
  // não tem ciclo de cobrança e atravessa os dois — ver `resolveCycleView`.
  const { activeCycle, showCycleSwitch, plans: filteredPlans } = useMemo(
    () => resolveCycleView(plans, selectedCycle),
    [plans, selectedCycle]
  );

  const enterprisePlan = filteredPlans[0];
  const hasCollabOption = collabPlans.length > 0;

  // A vitrine renderiza todos os planos do ciclo ativo, na ordem da escada.
  // Um plano novo cadastrado no admin aparece sozinho, na posição certa —
  // antes, o card pago era `find(p => p.signable)`, o primeiro assinável da
  // lista, e um plano novo tomava o lugar do que já estava lá.
  const showcasePlans = orderPlansForShowcase(filteredPlans);
  const highlightedId = findHighlighted(showcasePlans)?.id;

  return (
    creatorPlans.length > 0 && (
      <section id="pricing" className="w-full bg-tami-dark">
        <Grid>
          <GridItem mobile="full" tablet="full" desktop={12}>
            {/* Title */}
            <div className="text-center mb-10 lg:mb-14">
              <h2 className="text-3xl font-bold tracking-normal sm:text-6xl lg:text-7xl text-center mb-6 text-tami-light leading-tight">
                {HOME_COPY.pricing.title.line1}
                <br />
                {HOME_COPY.pricing.title.line2}
              </h2>

              <p className="text-lg text-tami-light/80 mb-8">
                {HOME_COPY.pricing.subtitle}
              </p>

              {/* Audience filter — espelha o seletor Criador/Collab do checkout */}
              {hasCollabOption && (
                <div className="mb-6 flex justify-center">
                  <div
                    role="tablist"
                    aria-label={HOME_COPY.pricing.audienceAria}
                    className="inline-flex gap-1 rounded-full border border-tami-light/20 bg-tami-light/5 p-1.5"
                  >
                    {AUDIENCE_TABS.map((tab) => {
                      const Icon = tab.icon;
                      const isSelected = audience === tab.id;

                      return (
                        <button
                          key={tab.id}
                          type="button"
                          role="tab"
                          aria-selected={isSelected}
                          onClick={() => setAudience(tab.id)}
                          className={cn(
                            "flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-bold transition-colors",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-tami-green focus-visible:ring-offset-2 focus-visible:ring-offset-tami-dark",
                            isSelected
                              ? "bg-tami-green text-tami-dark"
                              : "text-tami-light hover:bg-tami-light/10"
                          )}
                        >
                          <Icon className="size-4" />
                          {HOME_COPY.pricing.audienceTabs[tab.id]}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Cycle filter switch */}
              {showCycleSwitch && (
                <div className="flex justify-center items-center gap-4 mb-8">
                  <span className="text-sm font-medium text-tami-light">
                    {HOME_COPY.pricing.monthly}
                  </span>
                  <Switch
                    aria-label={HOME_COPY.pricing.cycleAnnualAria}
                    checked={activeCycle === PLAN_CYCLE.ANNUAL}
                    onCheckedChange={(checked) =>
                      setSelectedCycle(
                        checked ? PLAN_CYCLE.ANNUAL : PLAN_CYCLE.MONTHLY
                      )
                    }
                    className="data-[state=checked]:bg-tami-green"
                  />
                  <span className="text-sm font-medium text-tami-light">
                    {HOME_COPY.pricing.annual}
                  </span>
                </div>
              )}
            </div>

            {/* Grid — sem o switch de ciclo o respiro do topo vira vão morto */}
            <div
              className={cn(
                "flex justify-center",
                showCycleSwitch ? "mt-12" : "mt-2"
              )}
            >
              <div className="w-full max-w-[1400px] mx-auto">
                {audience === "agency" ? (
                  <Grid>
                    <GridItem mobile="full" tablet="full" desktop="full">
                      {/* Centrar via grid não funciona aqui: `col-d-*` emite o
                          shorthand `grid-column`, que vence qualquer
                          `col-start-*` na cascata e ancora o card na primeira
                          coluna. Largura em fração + auto margin resolve. */}
                      <div className="lg:mx-auto lg:w-10/12">
                        {enterprisePlan ? (
                          <EnterprisePricingCard plan={enterprisePlan} />
                        ) : null}
                      </div>
                    </GridItem>
                  </Grid>
                ) : (
                  /* Duas montagens da mesma vitrine.
                     Mobile: trilho horizontal com encaixe nativo — empilhado,
                     cada card tomava uma tela inteira e a seção virava rolagem
                     sem fim. O card a 86% deixa o vizinho aparecendo na borda,
                     que é o que anuncia que há mais planos ao lado.
                     No trilho os cards ficam `items-start`, cada um com a
                     própria altura — no celular só se vê um por vez, e um card
                     de lista curta não deve herdar a altura do vizinho.
                     A partir de `md`: grade, com `items-stretch` e sem
                     deslocamento vertical na coluna em destaque — as colunas
                     compartilham a linha de base, então topo, rodapé e CTAs se
                     alinham. O destaque é carregado por borda, halo e tarja.
                     O `-mx-4 px-4` sangra o trilho até a borda da tela sem
                     comer o respiro do primeiro e do último card, e o
                     `-my-4 py-4` abre espaço para o halo do destaque, que
                     `overflow-x-auto` recortaria por cima e por baixo. */
                  <div
                    className={cn(
                      "-mx-4 -my-4 flex snap-x snap-mandatory items-start gap-[22px] overflow-x-auto overscroll-x-contain px-4 py-4",
                      "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
                      "md:mx-0 md:my-0 md:grid md:snap-none md:grid-cols-2 md:items-stretch md:overflow-visible md:px-0 md:py-0 lg:grid-cols-3",
                    )}
                  >
                    {showcasePlans.map((plan, index) => {
                      const presentation = presentPlan(
                        plan,
                        index,
                        highlightedId
                      );

                      return (
                        <div
                          key={plan.id}
                          className="w-[86%] shrink-0 snap-center md:w-auto"
                        >
                          <PlanPricingCard
                            plan={plan}
                            highlighted={presentation.highlighted}
                            accent={presentation.accent}
                            badgeLabel={presentation.badgeLabel}
                            pitch={presentation.pitch}
                            ctaLabel={presentation.ctaLabel}
                            featuresExpanded={featuresExpanded}
                            onToggleFeatures={() =>
                              setFeaturesExpanded((estava) => !estava)
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            {/* End Grid */}

            {/* Ponte entre os dois públicos, para quem entrou na aba errada */}
            {hasCollabOption && (
              <p className="mt-6 text-center text-sm text-tami-light/60">
                {audience === "agency" ? (
                  <>
                    {HOME_COPY.pricing.bridge.toCreator}
                  </>
                ) : (
                  <>
                    {HOME_COPY.pricing.bridge.toCollab.before}{" "}
                    <span className="font-bold">
                      {HOME_COPY.pricing.bridge.toCollab.bold}
                    </span>{" "}
                    {HOME_COPY.pricing.bridge.toCollab.after}
                  </>
                )}
              </p>
            )}

            {/* Só no painel de creator: o de Collab tem um plano só, e comparar
                um plano com ele mesmo não é comparação. */}
            {audience !== "agency" && showcasePlans.length > 1 && (
              <PlanComparisonSection
                plans={showcasePlans}
                highlightedId={highlightedId}
              />
            )}
          </GridItem>
        </Grid>
      </section>
    )
  );
}
